"use client"

import type React from "react"

import { useState } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../components/ui/card"
import { Button } from "../../../components/ui/button"
import { Input } from "../../../components/ui/input"
import { Label } from "../../../components/ui/label"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../../components/ui/dialog"
import { ImageIcon, Plus, Edit, Trash2, Eye, GripVertical } from "lucide-react"

const slides = [
  {
    id: 1,
    title: "T.S.O Ministries",
    subtitle1: "Till every eye sees Jesus",
    subtitle2: "",
    backgroundImage: "https://tsoministries.com/wp-content/uploads/2025/06/IMG_2850--scaled.jpg",
    order: 1,
    active: true,
  },
  {
    id: 2,
    title: "Redeeming Love",
    subtitle1: "Saved by His sacrifice",
    subtitle2: "Accepted into the Beloved",
    backgroundImage: "https://tsoministries.com/wp-content/uploads/2025/06/IMG_2861--scaled.jpg",
    order: 2,
    active: true,
  },
  {
    id: 3,
    title: "Living Word",
    subtitle1: "Every promise fulfilled",
    subtitle2: "As Christ Is Revealed",
    backgroundImage: "https://tsoministries.com/wp-content/uploads/2025/06/IMG_2859-.jpg",
    order: 3,
    active: true,
  },
  {
    id: 4,
    title: "Eternal Light",
    subtitle1: "Shining in the darkness",
    subtitle2: "I carry That Light!",
    backgroundImage: "https://tsoministries.com/wp-content/uploads/2025/06/IMG_2857-.jpg",
    order: 4,
    active: true,
  },
  {
    id: 5,
    title: "Living Word",
    subtitle1: "Every promise fulfilled",
    subtitle2: "As Christ Is Revealed",
    backgroundImage: "https://tsoministries.com/wp-content/uploads/2025/06/IMG_2859-.jpg",
    order: 5,
    active: false,
  },
]

export function SliderControlSection({allUsers,adminUsers,comments}:any) {
  const [editingSlide, setEditingSlide] = useState<(typeof slides)[0] | null>(null)
  const [isAddingSlide, setIsAddingSlide] = useState(false)
  const [formData, setFormData] = useState({
    title: "",
    subtitle1: "",
    subtitle2: "",
    backgroundImage: "",
  })

  const handleEdit = (slide: (typeof slides)[0]) => {
    setEditingSlide(slide)
    setFormData({
      title: slide.title,
      subtitle1: slide.subtitle1,
      subtitle2: slide.subtitle2,
      backgroundImage: slide.backgroundImage,
    })
  }

  const handleAdd = () => {
    setIsAddingSlide(true)
    setFormData({
      title: "",
      subtitle1: "",
      subtitle2: "",
      backgroundImage: "",
    })
  }

  const handleSave = () => {
    console.log("Saving slide:", formData)
    setEditingSlide(null)
    setIsAddingSlide(false)
  }

  const handleDelete = (slideId: number) => {
    console.log("Deleting slide:", slideId)
  }

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      // In a real implementation, you would upload the file and get a URL
      const imageUrl = URL.createObjectURL(file)
      setFormData((prev) => ({ ...prev, backgroundImage: imageUrl }))
    }
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <ImageIcon className="h-5 w-5" />
                Homepage Slider Control
              </CardTitle>
              <CardDescription>Manage slides for the homepage banner carousel</CardDescription>
            </div>
            <Button onClick={handleAdd}>
              <Plus className="h-4 w-4 mr-2" />
              Add Slide
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {slides.map((slide) => (
              <Card key={slide.id} className={`${!slide.active ? "opacity-60" : ""}`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className="cursor-move">
                      <GripVertical className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <div className="flex-shrink-0">
                      <img
                        src={slide.backgroundImage || "/placeholder.svg"}
                        alt={slide.title}
                        className="w-24 h-16 object-cover rounded"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium truncate">{slide.title}</h3>
                      <p className="text-sm text-muted-foreground truncate">{slide.subtitle1}</p>
                      <p className="text-sm text-muted-foreground truncate">{slide.subtitle2}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span
                        className={`px-2 py-1 text-xs rounded ${
                          slide.active ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {slide.active ? "Active" : "Inactive"}
                      </span>
                      <Button variant="ghost" size="sm">
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(slide)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(slide.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>

      <Dialog
        open={editingSlide !== null || isAddingSlide}
        onOpenChange={() => {
          setEditingSlide(null)
          setIsAddingSlide(false)
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingSlide ? "Edit Slide" : "Add New Slide"}</DialogTitle>
            <DialogDescription>Configure the slide content and background image</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
                placeholder="Enter slide title"
              />
            </div>
            <div>
              <Label htmlFor="subtitle1">First Subtitle</Label>
              <Input
                id="subtitle1"
                value={formData.subtitle1}
                onChange={(e) => setFormData((prev) => ({ ...prev, subtitle1: e.target.value }))}
                placeholder="Enter first subtitle"
              />
            </div>
            <div>
              <Label htmlFor="subtitle2">Second Subtitle</Label>
              <Input
                id="subtitle2"
                value={formData.subtitle2}
                onChange={(e) => setFormData((prev) => ({ ...prev, subtitle2: e.target.value }))}
                placeholder="Enter second subtitle"
              />
            </div>
            <div>
              <Label htmlFor="background">Background Image</Label>
              <div className="space-y-2">
                <Input id="background" type="file" accept="image/*" onChange={handleImageUpload} />
                {formData.backgroundImage && (
                  <div className="mt-2">
                    <img
                      src={formData.backgroundImage || "/placeholder.svg"}
                      alt="Preview"
                      className="w-full h-32 object-cover rounded"
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setEditingSlide(null)
                  setIsAddingSlide(false)
                }}
              >
                Cancel
              </Button>
              <Button onClick={handleSave}>{editingSlide ? "Update Slide" : "Add Slide"}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
